const NEW_TAB_TARGET = "_blank";

type Navigate = (path: string) => void;

// Same-origin http(s) URLs become an app path; anything else returns null.
const toInAppPath = (url: string): string | null => {
  try {
    const parsed = new URL(url, window.location.href);
    const isHttp = parsed.protocol === "http:" || parsed.protocol === "https:";
    if (!isHttp || parsed.origin !== window.location.origin) return null;

    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
};

// On mobile, opens in-app new-tab links in the same tab; returns a cleanup.
export const keepInAppLinksInSameTab = (navigate: Navigate): (() => void) => {
  const originalOpen = window.open;

  window.open = new Proxy(originalOpen, {
    apply(target, thisArg, args: Parameters<typeof window.open>) {
      const [url, name] = args;
      const path = url ? toInAppPath(String(url)) : null;
      const isNewTab = !name || name === NEW_TAB_TARGET;

      if (path && isNewTab) {
        navigate(path);
        return null;
      }

      return Reflect.apply(target, thisArg, args);
    },
  });

  const handleClick = (event: MouseEvent) => {
    if (event.defaultPrevented || !(event.target instanceof Element)) return;

    const anchor = event.target.closest<HTMLAnchorElement>(
      `a[target="${NEW_TAB_TARGET}"]`
    );
    const path = anchor ? toInAppPath(anchor.href) : null;
    if (!path) return;

    event.preventDefault();
    navigate(path);
  };

  document.addEventListener("click", handleClick, true);

  return () => {
    window.open = originalOpen;
    document.removeEventListener("click", handleClick, true);
  };
};
